import { connect } from './redux/store';
import { compose, lifecycle } from 'recompose';
import moment from 'moment';
import { List } from 'immutable';
import { Utils, C } from '@kineticdata/bundle-common';

export const isBlank = string => !string || string.length === 0;

export const isActiveClass = defaultClass => props => ({
  className: props.isCurrent ? `${defaultClass} active` : defaultClass,
});

export const getDueDate = (submission, attrName) => {
  const daysDue = Utils.getConfig({ submission, name: attrName });
  if (!daysDue) {
    throw new Error(`getDueDate failed because "${attrName}" was not set.`);
  }
  const daysDueNumber = parseInt(daysDue, 10);
  if (!daysDueNumber) {
    throw new Error(
      `getDueDate failed because value of "${attrName}" (${daysDue}) is not a number`,
    );
  }
  return submission.submittedAt
    ? moment(submission.submittedAt).add(daysDueNumber, 'days')
    : null;
};

export const getDurationInDays = (start, end) =>
  Math.round(moment(end).diff(start, 'days', true) * 10) / 10;

export const getStatus = submission => {
  if (!submission.values) {
    throw new Error(
      'getStatus failed because values were not included on ' +
        'the submission.',
    );
  }
  return submission.values[C.FIELD_STATUS] || submission.coreState;
};

export const getRequester = submission => {
  if (!submission.values) {
    throw new Error(
      'getRequester failed because values were not included on ' +
        'the submission.',
    );
  }
  return (
    submission.values[C.FIELD_REQUESTED_BY] || submission.submittedBy
  );
};

export const getStatusClass = ({ values, form, coreState }) => {
  if (
    !values ||
    !form ||
    !form.attributes ||
    !form.kapp ||
    !form.kapp.attributes
  ) {
    throw new Error(
      'getStatusClass failed because the submission did not ' +
        'have the required includes (values,form.attributes,form.kapp.attributes)',
    );
  }
  const statusFieldValue = values[C.FIELD_STATUS];
  if (statusFieldValue) {
    const activeStatuses = Utils.getAttributeValues(
      form,
      C.ATTR_STATUSES_ACTIVE,
      Utils.getAttributeValues(form.kapp, C.ATTR_STATUSES_ACTIVE, []),
    );
    const inactiveStatuses = Utils.getAttributeValues(
      form,
      C.ATTR_STATUSES_INACTIVE,
      Utils.getAttributeValues(form.kapp, C.ATTR_STATUSES_INACTIVE, []),
    );
    const cancelledStatuses = Utils.getAttributeValues(
      form,
      C.ATTR_STATUSES_CANCELLED,
      Utils.getAttributeValues(form.kapp, C.ATTR_STATUSES_CANCELLED, []),
    );
    if (activeStatuses.includes(statusFieldValue)) {
      return C.SUCCESS_LABEL_CLASS;
    } else if (inactiveStatuses.includes(statusFieldValue)) {
      return C.WARNING_LABEL_CLASS;
    } else if (cancelledStatuses.includes(statusFieldValue)) {
      return C.DANGER_LABEL_CLASS;
    } else {
      return C.DEFAULT_LABEL_CLASS;
    }
  } else {
    switch (coreState) {
      case C.CORE_STATE_DRAFT:
        return C.WARNING_LABEL_CLASS;
      case C.CORE_STATE_SUBMITTED:
        return C.SUCCESS_LABEL_CLASS;
      default:
        return C.DEFAULT_LABEL_CLASS;
    }
  }
};

export const getStatusColor = props => {
  switch (getStatusClass(props)) {
    case C.SUCCESS_LABEL_CLASS:
      return 'info';
    case C.WARNING_LABEL_CLASS:
      return 'warning';
    case C.DANGER_LABEL_CLASS:
      return 'danger';
    default:
      return 'subtle';
  }
};

export const getRequestStatusColor = ({ coreState }) => {
  switch (coreState) {
    case C.CORE_STATE_SUBMITTED:
      return 'info';
    case C.CORE_STATE_DRAFT:
      return 'warning';
    default:
      return 'subtle';
  }
};
export const getActionStatusColor = ({ coreState }) => {
  switch (coreState) {
    case C.CORE_STATE_DRAFT:
      return 'info';
    default:
      return 'subtle';
  }
};

export const getSubmissionPath = (appLocation, submission, mode, listType) => {
  return [
    appLocation,
    'requests',
    listType,
    'request',
    submission.id,
    mode ||
      (submission.coreState === C.CORE_STATE_DRAFT ? '' : 'activity'),
  ]
    .filter(s => !!s)
    .join('/');
};

export const getApprovalPath = (appLocation, submission, listType) => {
  return [appLocation, 'approvals', listType, 'approval', submission.id]
    .filter(s => !!s)
    .join('/');
};

export const getActionPath = (appLocation, submission, listType) => {
  return [appLocation, 'actions', listType, 'action', submission.id]
    .filter(s => !!s)
    .join('/');
};

export const expandSLTValue = value =>
  value.replace(/(\d+)([wdhm])/g, (match, num, char) => {
    const suffix = parseInt(num, 10) === 1 ? '' : 's';
    const word =
      char === 'w'
        ? 'week'
        : char === 'd'
          ? 'day'
          : char === 'h'
            ? 'hour'
            : 'minute';
    return `${num} ${word}${suffix}`;
  });

export const parsePVGOverrideAttribute = (value, me, invalid) => {
  let data = null;
  try {
    data = JSON.parse(value);
  } catch (e) {}
  if (data && data['Team'] && data['Form Slug']) {
    return {
      valid: !invalid && !!me.memberships.find(m => m.team.name === data.Team),
      formSlug: data['Form Slug'],
    };
  } else {
    return {
      valid: false,
      formSlug: null,
    };
  }
};

// Attempts to parse the activities data property as JSON, if successful it
// returns that otherwise it returns the string value wrapped in an object.
export const parseActivityData = activity => {
  try {
    return activity.data ? JSON.parse(activity.data) : {};
  } catch (e) {
    return { STRING: activity.data };
  }
};

export const displayableFormPredicate = form =>
  form.type === 'Service' && form.status === 'Active';

export const getCatalogRevisionLockData = (revision, username) => {
  if (!revision) {
    return {};
  }
  const lockedUntil =
    new Date(revision.updatedAt).getTime() + C.REVISION_LOCK_TIME;
  const now = new Date().getTime();
  const isLocked = !!revision.values['Locked By'] && now < lockedUntil;
  return {
    isLocked,
    isLockedByMe: isLocked && revision.values['Locked By'] === username,
    lockTimeLeft: isLocked ? lockedUntil - now : 0,
  };
};

export const connectUser = (Component, mapPropsToUsername) =>
  compose(
    connect((state, props) => {
      const usernameToConnect = mapPropsToUsername(props);
      return {
        usernameToConnect,
        user: state.app.usersMap.get(usernameToConnect),
        fetchUsers: state.app.actions.fetchUsers,
      };
    }),
    lifecycle({
      componentDidMount() {
        if (typeof this.props.user === 'undefined') {
          this.props.fetchUsers({ usernames: [this.props.usernameToConnect] });
        }
      },
    }),
  )(Component);

export const connectSubteam = (Component, mapPropsToTeamName) =>
  compose(
    connect((state, props) => {
      const teamNameToConnect = mapPropsToTeamName(props);
      const parentTeamName = teamNameToConnect.split('::')[0];
      return {
        teamNameToConnect,
        team: (state.app.subteamsMap.get(parentTeamName) || List()).find(
          t => t.name === teamNameToConnect,
        ),
        fetchSubteams: state.app.actions.fetchSubteams,
      };
    }),
  )(Component);
